/**
 * Native location-permission bootstrap for the Capacitor (Android) build.
 *
 * On a phone the WebView's `navigator.geolocation` — which DeviceGnssSource uses
 * — is gated behind the OS runtime location permission. The browser watch won't
 * raise that Android permission dialog itself, so on a native build we ask for it
 * through the Capacitor Geolocation plugin first. Once it's granted, the existing
 * navigator.geolocation watch gets fixes exactly as it does on a laptop.
 *
 * On the web this is a no-op: the browser prompts on watchPosition itself, so we
 * return 'granted' straight away and let the normal flow run. The plugin import
 * is dynamic so nothing native is pulled into the web bundle's start-up path.
 */
import { Capacitor } from '@capacitor/core';

export type NativePermissionResult = 'granted' | 'denied' | 'unavailable';

export async function ensureNativeLocationPermission(): Promise<NativePermissionResult> {
  // Web (or any non-native host): the browser handles its own permission prompt.
  if (!Capacitor.isNativePlatform()) return 'granted';
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    const current = await Geolocation.checkPermissions();
    if (current.location === 'granted' || current.coarseLocation === 'granted') return 'granted';
    const asked = await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
    return asked.location === 'granted' || asked.coarseLocation === 'granted' ? 'granted' : 'denied';
  } catch {
    // Plugin missing or threw: don't block the watch — let navigator.geolocation
    // try, so we degrade to the web behaviour rather than dead-ending.
    return 'unavailable';
  }
}
